import type { FallbackProps } from '@gik/core/api/BaseAPIConfig';
import { withTitle } from '@gik/core/hocs/withTitle';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import type { IHomePageProps } from '@gik/landing-pages/pages/HomePage';
import type { ILandingPageProps } from '@gik/landing-pages/pages/LandingPage';
import { LandingPage } from '@gik/landing-pages/pages/LandingPage';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { PageLayout } from '@/components/layout/PageLayout';
import { SWRConfig } from 'swr';

export default function LandingPageTemplateComp({ fallback }: FallbackProps<IHomePageProps>) {
  return withTitle(
    'Organize Gift Cards',
    <SWRConfig value={{ fallback }}>
      <LandingPage {...landingPageProps} url="/flexible-gift-cards" />
    </SWRConfig>
  );
}

LandingPageTemplateComp.getLayout = function getLayout(page, layoutProps: PageLayoutProps) {
  return (
    <PageLayout {...layoutProps} key={layoutProps.key}>
      {page}
    </PageLayout>
  );
};

// FIXME: Replace this strategy with i18n library instead
const landingPageProps: ILandingPageProps = {
  heroSection: {
    hero: {
      title: 'Flexible Gift Cards',
      content:
        'Give InKind is the leader in gift card support. Organize a gift card train, collect and pool for distribution, and more. Every gift is flexible, secure, and trackable.',
    },
    backgroundImage: `${getAssetsUrl()}landing-pages/headers/hero-senior-mobile@2x.png`,
    backgroundImageMd: `${getAssetsUrl()}landing-pages/headers/hero-senior-desktop@2x.png`,
    backgroundColor: '#f8f5f7',
  },
  featureFocus: {
    feature: {
      title: 'Why Choose Give InKind?',
      subtitle:
        'Give InKind is different. No other website allows gift recipients the freedom to choose how or where they use their gifts.',
      icon: `${getAssetsUrl()}landing-pages/GC-Main-1.png`,
      features: [
        <>
          <span className={'tw-font-bold'}>Practical Gift Cards for Essential Needs:</span> Cover meals, groceries,
          housing, essentials, and more through major national retailers.
        </>,
        <>
          <span className={'tw-font-bold'}>Flexible Gifting That Adapts to Changing Needs:</span> Gift cards can easily
          be pooled, distributed as needed, or exchanged, ensuring support always matches the recipient&apos;s evolving
          circumstances.
        </>,
        <>
          <span className={'tw-font-bold'}>Easy to Manage and Redeem:</span> Recipients can conveniently store, track,
          and use gift cards directly within their Give InKind Wallet.
        </>,
        <>
          <span className={'tw-font-bold'}>Ideal for Long-Distance Support:</span> Send meaningful help from anywhere,
          ensuring loved ones receive exactly the support they need, no matter where they live.
        </>,
        <>
          <span className={'tw-font-bold'}>Secure, Centralized Platform:</span> Safely purchase, store, and send gift
          cards from one simple, reliable location.
        </>,
      ],
    },
  },
  ctaSection: {
    ctas: [
      {
        title: 'The Give InKind Wallet',
        content:
          'No more lost or stolen gifts. Never worry about getting the wrong thing. Only on Give InKind is every gift able to be tracked, pooled, swapped, and more.',
        icon: `${getAssetsUrl()}landing-pages/features/GC-Feature01.png`,
        buttonTitle: 'Read More',
        buttonLink: '/articles/introducing-give-inkind-wallet',
      },
      {
        title: 'How to Start a Gift Card Train',
        content: `Get support where it's needed, and fast. We make it easy to collect and distribute gift cards in less than 5 minutes.`,
        icon: `${getAssetsUrl()}landing-pages/features/GC-Feature02.png`,
        buttonTitle: 'Read More',
        buttonLink: '/articles/build-gift-card-train',
      },
      {
        title: '5 Best Gift Cards to Send to Parents After the Birth of a New Baby',
        content:
          'Experts break down the most popular and helpful gift cards to send your friends and loved ones for every situation.',
        icon: `${getAssetsUrl()}landing-pages/features/GC-Feature03.png`,
        buttonTitle: 'Read More',
        buttonLink: '/articles/5-best-gift-cards-send-parents-birth-new-baby',
      },
    ],
  },
  blogSection: {
    mainPost: {
      title: `Top 5 Gift Cards Families Need in Catastrophic Home Loss`,
      content: `Explore essential gift cards that provide immediate relief and practical support to families rebuilding after a devastating loss.`,
      icon: 'https://cms.giveinkind.com/wp-content/uploads/2020/06/house-fire-1548280_1920-600x600.jpg',
      buttonTitle: 'Read more',
      buttonLink: '/articles/top-5-gift-cards-families-need-catastrophic-home-loss',
    },
    posts: [
      {
        title: `Why Gift Cards Are Useful in a Crisis`,
        content: `Learn why gift cards are one of the most helpful and practical ways to provide immediate, flexible support to friends or family experiencing a crisis or emergency.`,
        buttonTitle: 'Read more',
        buttonLink: '/articles/why-gift-cards-are-useful-in-crisis',
      },
      {
        title: `Best Gift Cards for Your Holiday Gift Card Drive`,
        content: `Discover the best gift cards to include in your holiday gift card drive, featuring popular brands that recipients truly need and appreciate during the festive season.`,
        buttonTitle: 'Read more',
        buttonLink: '/articles/best-gift-cards-holiday-gift-card-drive',
      },
      {
        title: `Why Digital Gift Cards Can Be Surprisingly Personal`,
        content: `Explore how digital gift cards offer thoughtful, personalized support that perfectly meets recipients' unique needs and preferences.`,
        buttonTitle: 'Read more',
        buttonLink: '/articles/why-digital-gift-cards-are-personal',
      },
      {
        title: `Best Gift Cards for Families Facing Pediatric Cancer`,
        content: `Discover the most helpful gift cards to ease daily burdens and provide meaningful, practical support to families navigating pediatric cancer treatment.`,
        buttonTitle: 'Read more',
        buttonLink: '/articles/best-gift-cards-for-families-facing-pediatric-cancer',
      },
      {
        title: `Best Gift Cards to Send a Teammate Welcoming a New Baby`,
        content: `Explore thoughtful, practical gift cards perfect for supporting coworkers and teammates as they adjust to life with a new baby.`,
        buttonTitle: 'Read more',
        buttonLink: '/articles/best-gift-cards-send-teammate-welcoming-new-baby',
      },
      {
        title: `How To Boost Your Organization’s Fundraising Drives with a Gift Card Train`,
        content: `Learn how creating a Gift Card Train can supercharge your fundraising efforts, inspiring greater participation and more impactful donations.`,
        buttonTitle: 'Read more',
        buttonLink: '/articles/boost-organizations-fundraising-drives-gift-card-train',
      },
    ],
  },
};
